import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

export const ACCESS_TOKEN_KEY = 'auth_token';
export const REFRESH_TOKEN_KEY = 'auth_refresh_token';

const api = axios.create({
  baseURL: '/api',
  // Fail fast instead of spinning forever when the backend stalls. Quotes
  // typically resolve in well under 5s; 60s is well above the slowest legit
  // path and still surfaces a clear error to the user.
  timeout: 60000,
});

/** Marque une requête déjà rejouée après rafraîchissement (une seule fois). */
type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

/**
 * Routes d'authentification à ne JAMAIS rejouer après rafraîchissement.
 *
 * Un 401 y est la réponse métier attendue (mauvais mot de passe, refresh
 * expiré/rejoué), pas un access token périmé : tenter un refresh reboucle,
 * et sur `/auth/refresh` lui-même cela partirait en récursion.
 */
const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/signup',
  '/auth/refresh',
  '/auth/logout',
  // Code SSO à usage unique : un 401 est le refus du portail, le rejouer
  // après un refresh ne ferait que brûler un code déjà consommé.
  '/auth/sso/callback',
];

function isAuthPath(url?: string): boolean {
  if (!url) return false;
  return NO_REFRESH_PATHS.some((path) => url.includes(path));
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/** Enregistre le couple issu d'un login, d'une inscription ou d'une rotation. */
export function setSession(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Rafraîchissement en vol, partagé par tous les appelants.
 *
 * L'access token étant court (15 min), il expire typiquement pendant qu'un
 * écran tire cinq requêtes en parallèle : les cinq prennent un 401 en même
 * temps. Sans ce verrou, chacune appellerait `/auth/refresh` avec le MÊME
 * refresh token — or la rotation le révoque au premier appel, et les quatre
 * suivants seraient donc vus par le serveur comme une réutilisation, ce qui
 * coupe toute la famille de sessions. D'où le déconnexions aléatoires que le
 * verrou évite : un seul appel réseau, les autres attendent son résultat.
 */
let refreshInFlight: Promise<string> | null = null;

/** Appelé quand la session ne peut plus être rétablie. Surchargé par les tests. */
function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  window.location.href = '/login';
}

async function requestNewTokenPair(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  // Client axios nu : passer par `api` ferait repasser la réponse dans cet
  // intercepteur, donc un 401 sur le refresh relancerait un refresh.
  const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
    '/api/auth/refresh',
    { refreshToken },
    { timeout: 15000 },
  );
  setSession(data.accessToken, data.refreshToken);
  return data.accessToken;
}

export function refreshSession(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokenPair().finally(() => {
      // Libéré dès que la promesse est réglée : le prochain 401 (plus tard)
      // repartira sur un rafraîchissement neuf, avec le jeton tourné.
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    // **On n'éjecte jamais quelqu'un qui n'a pas de session.**
    //
    // Les pages publiques — portail de cotation, suivi d'ordre, coffre
    // documentaire — sont ouvertes par un transporteur **sans compte**. Un
    // appel authentifié parti au passage (le branding, par exemple) y prend un
    // 401 parfaitement normal. Tenter un rafraîchissement puis renvoyer sur
    // /login pour autant transformait le lien tokenisé en mur de connexion —
    // exactement ce que l'architecture de l'espace transporteur interdit. Sans
    // jeton il n'y a rien à rafraîchir, rien à purger et personne à déconnecter.
    if (error.response?.status === 401 && !getAccessToken()) {
      return Promise.reject(error);
    }

    if (
      error.response?.status !== 401 ||
      !config ||
      config._retriedAfterRefresh ||
      isAuthPath(config.url)
    ) {
      // 401 non rattrapable : on ne purge que si la session était censée être
      // active, pour ne pas effacer les jetons sur un login raté.
      if (error.response?.status === 401 && !isAuthPath(config?.url)) {
        clearSession();
        redirectToLogin();
      }
      return Promise.reject(error);
    }

    config._retriedAfterRefresh = true;

    let accessToken: string;
    try {
      accessToken = await refreshSession();
    } catch {
      // Refresh expiré, révoqué ou rejoué : la session est définitivement
      // perdue, on purge et on renvoie l'utilisateur au login.
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    config.headers.Authorization = `Bearer ${accessToken}`;
    return api.request(config);
  },
);

export default api;
